import axios, { AxiosError, InternalAxiosRequestConfig } from "axios"
import { LocalStorageSession } from "@/shared/utils/local-storage-session"
import { MsalAuthService } from "@features/auth/services/auth.service.ts"
import { loginMicrosoft } from "@features/auth/hooks/auth.queries"
import { requestReLogin } from "@features/auth/services/session-expired"

let isRefreshing = false
let refreshQueue: Array<{ resolve: (value: InternalAxiosRequestConfig) => void; reject: (reason?: Error | AxiosError) => void; config?: InternalAxiosRequestConfig }> = []

const REFRESH_TIMEOUT_MS = 10_000

/**
 * Resuelve con `fallback` si `promise` no se resuelve dentro de `ms`.
 * Evita que un acquireTokenSilent de MSAL colgado (p.ej. por bloqueo de cookies de terceros)
 * deje `isRefreshing` en true para siempre y congele todas las requests de la app.
 */
const withTimeout = <T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      () => {
        clearTimeout(timer)
        resolve(fallback)
      }
    )
  })

/**
 * Process the queue of failed requests with the new token
 * @param token The new access token
 * @param error Error if token refresh failed
 */
const processQueue = (token: string | null, error?: AxiosError) => {
  for (const request of refreshQueue) {
    if (token && request.config) {
      request.config.headers.Authorization = `Bearer ${token}`
      request.resolve(request.config)
    } else {
      request.reject(error ?? new Error("El refresco del token falló"))
    }
  }

  refreshQueue = []
}

/**
 * Attempt to refresh the token using Microsoft authentication
 * @returns A promise that resolves to the new token or null if refresh failed
 */
const refreshToken = async (): Promise<string | null> => withTimeout(refreshTokenAttempt(), REFRESH_TIMEOUT_MS, null)

const refreshTokenAttempt = async (): Promise<string | null> => {
  try {
    const userJson = localStorage.getItem("current_user")
    if (!userJson) return null

    const userData = JSON.parse(userJson)

    // Si es un usuario de Microsoft, usamos MSAL para refrescar
    if (userData.tenantId && userData.email) {
      const msalInstance = await MsalAuthService.getMsalInstance()
      if (!msalInstance) return null

      const accounts = msalInstance.getAllAccounts()
      const account = accounts.find((acc) => acc.username === userData.email) || accounts[0]
      if (!account) return null

      const tokenResponse = await msalInstance.acquireTokenSilent({ scopes: ["openid", "profile", "email"], account })
      if (!tokenResponse || !tokenResponse.idToken) return null

      const loginResponse = await loginMicrosoft({ email: userData.email, useMicrosoft: true, microsoftToken: tokenResponse.idToken, tenantId: userData.tenantId })

      LocalStorageSession.saveAuthorization(loginResponse.user, loginResponse.accessToken)

      return loginResponse.accessToken
    }

    // Para otros usuarios (proveedores), intentamos el refresco directo del backend si el token no ha expirado aún según el servidor
    // o simplemente retornamos null para que la petición original proceda y el servidor decida (401)
    return null
  } catch {
    return null
  }
}

const getStoredUser = (): { email?: string; tenantId?: string } | null => {
  try {
    return JSON.parse(localStorage.getItem("current_user") ?? "null")
  } catch {
    return null
  }
}

/**
 * Si el refresco silencioso falla, un usuario de Microsoft vuelve a ingresar desde un modal sin salir de la página,
 * así no pierde lo que estaba llenando. Devuelve null para otros usuarios o si eligió cerrar sesión.
 */
const reLoginMicrosoftUser = async (): Promise<string | null> => {
  const user = getStoredUser()
  if (!user?.tenantId || !user.email || globalThis.location.pathname.includes("/login")) return null
  return requestReLogin(user.email, user.tenantId)
}

type RetriableConfig = InternalAxiosRequestConfig & { _retriedAfterRefresh?: boolean }

const processUnauthorized = async (error: AxiosError) => {
  const isAuthUrl = error?.config?.url?.includes("/auth/loginMicrosoft") || error?.config?.url?.includes("/auth/loginProvider") || error?.config?.url?.includes("/auth/login") || error?.config?.url?.includes("/auth/logout")
  const config = error.config as RetriableConfig | undefined

  if (!isAuthUrl && !config?._retriedAfterRefresh) {
    if (isRefreshing) return new Promise<InternalAxiosRequestConfig>((resolve, reject) => refreshQueue.push({ resolve, reject, config })).then((queuedConfig) => axios({ ...queuedConfig, _retriedAfterRefresh: true } as RetriableConfig))
    else {
      isRefreshing = true
      try {
        const newToken = (await refreshToken()) ?? (await reLoginMicrosoftUser())

        processQueue(newToken)

        if (newToken && config) {
          config.headers.Authorization = `Bearer ${newToken}`
          config._retriedAfterRefresh = true
          return axios(config)
        }

        const userData = getStoredUser()

        // Los usuarios de Microsoft ya pasaron por el modal (si eligieron cerrar sesión, requestReLogin redirige)
        if (userData && !userData.tenantId) {
          await LocalStorageSession.clearSession()
          globalThis.location.href = "/login"
        }
      } catch (refreshError) {
        console.error("Token refresh error:", refreshError)

        processQueue(null, refreshError as AxiosError)

        const userJson = localStorage.getItem("current_user")
        if (userJson) {
          await LocalStorageSession.clearSession()
          globalThis.location.href = "/login"
        }
      } finally {
        isRefreshing = false
      }
    }
  }
}

/**
 * Configure global axios defaults and interceptors
 * This ensures that all axios requests, even direct ones not using the AxiosHttpClient,
 * will include the JWT token in the Authorization header
 */
export const configureAxios = () => {
  // Add request interceptor to include JWT token in all requests
  axios.interceptors.request.use(
    async (config) => {
      // Escenarios donde retorno el config para que se ejecute con normalidad
      // 1. Si el request es para el login o el logout, no se incluye el token, entonces retornas el config para que se ejecute con normalidad
      const isAuthRequest = config.url?.includes("/auth/loginMicrosoft") || config.url?.includes("/auth/loginProvider") || config.url?.includes("/auth/login")
      if (isAuthRequest) return config

      // 2. Si no es login y logout, verifico si el token ha expirado
      // 2.1. Si no expiro, ejecutar el request agregando el token en la cabecera y continuar con el flujo del request
      if (!LocalStorageSession.isTokenExpired()) {
        const token = LocalStorageSession.getAccessToken()
        if (token) {
          config.headers = config.headers || {}
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      }

      // 2.2. Si expiro:
      // 2.2.1. Don't attempt to refresh during auth requests or if already refreshing
      if (isRefreshing) return new Promise<InternalAxiosRequestConfig>((resolve, reject) => refreshQueue.push({ resolve, reject, config }))

      isRefreshing = true

      try {
        // Si el refresco silencioso falla, el usuario de Microsoft vuelve a ingresar desde el modal y la petición continúa
        const newToken = (await refreshToken()) ?? (await reLoginMicrosoftUser())

        // Process the queue with the new token
        processQueue(newToken)

        if (newToken) {
          config.headers.Authorization = `Bearer ${newToken}`
          return config
        }

        if (getStoredUser()?.tenantId) throw new Error("El token expiró y el usuario no volvió a iniciar sesión")

        // Si no es Microsoft, permitimos que la petición original continúe.
        // Si el token es realmente inválido, el servidor responderá 401 y se manejará en el interceptor de respuesta.
        return config
      } finally {
        isRefreshing = false
      }
    },
    (error) => error as AxiosError
  )

  // Add response interceptor for handling refreshed tokens and errors
  axios.interceptors.response.use(
    (response) => {
      const refreshedToken = response.headers["x-refreshed-token"]
      if (refreshedToken) {
        const userJson = localStorage.getItem("current_user")
        if (userJson) {
          const userData = JSON.parse(userJson)
          LocalStorageSession.saveAuthorization(userData, refreshedToken)
          console.log("Token refreshed by server")
        }
      }
      return response
    },
    async (error) => {
      if (error?.response?.status === 401) {
        const retried = await processUnauthorized(error)
        if (retried) return retried
      }

      return Promise.reject(error as AxiosError)
    }
  )
}

// Call this function as early as possible in the application lifecycle
configureAxios()
